import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useDuplicateTemplate } from 'modules/manager/ui/hooks/use-duplicate-template';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerDuplicateTemplateForm, {
  DUPLICATE_TEMPLATE_INITIAL_VALUES,
  DuplicateTemplateFormValues,
} from 'modules/manager/ui/components/forms/media-manager-duplicate-template-form';

interface Args {
  /** Called with the fresh copy once it exists (the modal is already closed). */
  onDuplicated: (copy: MediaManagerTemplate) => void;
}

/**
 * "Duplicate into my tournament" flow: asks for the copy's name (placeholder =
 * the source name) and blocks the submit while the copy is being created.
 */
function useDuplicateTemplateModal({ onDuplicated }: Args) {
  const { duplicate, canDuplicate, isDuplicating, error, resetError } = useDuplicateTemplate();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(
    (next: MediaManagerTemplate) => {
      resetError();
      setTemplate(next);
      setIsOpen(true);
    },
    [resetError],
  );
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: DuplicateTemplateFormValues) => {
      if (!template) return;
      const copy = await duplicate(template.id, { name: values.name });
      if (!copy) return;
      setIsOpen(false);
      onDuplicated(copy);
    },
    [template, duplicate, onDuplicated],
  );

  return {
    open,
    close,
    canDuplicate,
    isDuplicating,
    modal: (
      <Modal show={isOpen} onClose={isDuplicating ? () => undefined : close}>
        <UDText type="title-3" className="mb-5">
          Дублировать шаблон
        </UDText>
        <MediaManagerDuplicateTemplateForm
          // Reset the field whenever another template is picked.
          key={template?.id ?? 'none'}
          initialValues={DUPLICATE_TEMPLATE_INITIAL_VALUES}
          sourceName={template?.name ?? 'Название шаблона'}
          isLoading={isDuplicating}
          errorMessage={error}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useDuplicateTemplateModal;
